import api from '../../api/client';
import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Form,
  Row,
  Col,
  Select,
  DatePicker,
  InputNumber,
  Input,
  Button,
  Space,
  Popconfirm,
  message,
  Modal,
  Divider,
  Typography,
  Dropdown,
  ConfigProvider,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  FileTextOutlined,
  DownOutlined,
  PlusCircleOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import config from "../../config";
import { useSelector } from "react-redux";
import LineRow from "./LineRow";
import StakeholderModal from "../../components/StakeholderModal";
import { Link } from "react-router-dom";
import { apiFetch, fetchSuppliers, fetchUsers } from "../../api";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Helper functions
const fmt = (n, d = 2) =>
  Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });
const genKey = () =>
  `r_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

const AssetPurchaseInvoice = ({ onSuccess, onCancel }) => {
  const [headerForm] = Form.useForm();
  const [lineItems, setLineItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [assetTypes, setAssetTypes] = useState([]);
  const [farms, setFarms] = useState([]);
  const [silos, setSilos] = useState([]);
  // const [stakeholders, setStakeholders] = useState([]);
  const [subtypeMap, setSubtypeMap] = useState({});
  const [stakeholderModalVisible, setStakeholderModalVisible] = useState(false);
  const [selectedStakeholder, setSelectedStakeholder] = useState(null);

  const currentUser = useSelector((state) => state.account.user);
  const environment =
    window.location.hostname === "localhost" ? "development" : "production";
  const API = api.defaults.baseURL || config[environment].apiUrl;

  const [deliveredMethod, setDeliveredMethod] = useState("delivered");

  const [users, setUsers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  // Load configuration
  const loadConfig = useCallback(async () => {
    try {
      const [tr, fr, sr, shr, subr] = await Promise.all([
        apiFetch("/api/animal-assets/config/types"),
        apiFetch(`/farm`),
        apiFetch("/api/animal-assets/config/silos"),
        apiFetch(`/suppliers`),
        apiFetch("/api/animal-assets/config/subtypes"),
      ]);
      setAssetTypes(tr.data || []);
      setFarms(fr || []);
      setSilos(sr.data || []);
      // setStakeholders(shr || []);
      const map = {};
      (subr.data || []).forEach((s) => {
        if (!map[s.slug]) map[s.slug] = [];
        map[s.slug].push(s);
      });
      setSubtypeMap(map);
    } catch (e) {
      message.error("Config load failed");
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const loadSuppliers = async () => {
    try {
      const data = await fetchSuppliers();
      setSuppliers(data);
    } catch (error) {
      console.error("Error loading suppliers:", error);
    }
  };
  const loadUsers = async () => {
    try {
      const data = await fetchUsers();
      setUsers(data);
    } catch (error) {
      console.error("Error loading users:", error);
    }
  };
  useEffect(() => {
    loadSuppliers();
    loadUsers();
    // loadItems();
  }, []);

  // Handle stakeholder added
  const handleStakeholderAdded = (newStakeholder) => {
    // setStakeholders((prev) => [...prev, newStakeholder]);
    loadSuppliers();
    setSuppliers((prev) => [...prev, newStakeholder]);
    setSelectedStakeholder(newStakeholder.id);
    headerForm.setFieldsValue({ stakeholderId: newStakeholder.id });
    message.success(`Supplier ${newStakeholder.name} added successfully`);
  };

  // Add row
  const addRow = async (slug) => {
    const cfg = assetTypes.find((t) => t.slug === slug) || {};
    setLineItems((p) => [
      ...p,
      {
        _key: genKey(),
        type_slug: slug || null,
        subtype_id: null,
        category: cfg.is_livestock !== false ? "livestock" : "physical",
        animalName: "",
        tagNumber: null,
        batchId: null,
        quantity: 1,
        cost: 0,
        totalCost: 0,
        gender: null,
        birth: null,
        farm: null,
        siloId: null,
        assetName: "",
        serialNumber: "",
        usefulLifeYears: cfg.default_life_years || null,
      },
    ]);
  };

  // Update row
  const updateRow = (i, v) =>
    setLineItems((p) =>
      p.map((r, idx) => {
        if (idx !== i) return r;
        const cfg = assetTypes.find((t) => t.slug === v.type_slug) || {};
        return {
          ...v,
          totalCost: (cfg.is_batch ? v.quantity || 1 : 1) * (v.cost || 0),
        };
      }),
    );

  // Remove row
  const removeRow = (i) => setLineItems((p) => p.filter((_, idx) => idx !== i));

  // Preview tag
  const previewTag = useCallback(async (slug, farmId) => {
    const d = await apiFetch(
      `/api/animal-assets/next-tag?slug=${slug}${
        farmId ? `&farm_id=${farmId}` : ""
      }`,
    );
    return d.tag;
  }, []);

  // Save invoice
  const saveInvoice = async () => {
    if (!lineItems.length) {
      message.error("Add at least one line item.");
      return;
    }
    if (lineItems.some((r) => !r.type_slug || !r.cost)) {
      message.error("Each item needs type and cost.");
      return;
    }
    try {
      const header = await headerForm.validateFields();
      setSaving(true);
      const d = await apiFetch("/api/animal-assets", {
        method: "POST",
        body: JSON.stringify({
          header: {
            ...header,
            recorded: header.recorded?.format("YYYY-MM-DD"),
            dueDate: header.dueDate?.format("YYYY-MM-DD"),
            userId: currentUser?.id,
            userName: currentUser?.firstname,
          },
          items: lineItems.map((r) => {
            const cfg = assetTypes.find((t) => t.slug === r.type_slug) || {};
            return {
              ...r,
              totalCost: (cfg.is_batch ? r.quantity || 1 : 1) * (r.cost || 0),
            };
          }),
        }),
      });
      message.success(
        `Invoice ${d.transactionId} saved — ${
          d.insertedIds.length
        } asset(s). Tags: ${d.insertedIds.map((i) => i.tag).join(", ")}`,
      );
      headerForm.resetFields();
      setLineItems([]);
      if (onSuccess) onSuccess(d);
    } catch (e) {
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Add line menu
  const addLineMenu = {
    items: [
      {
        key: "__h",
        label: (
          <Text type="secondary" style={{ fontSize: 11 }}>
            Quick add:
          </Text>
        ),
        disabled: true,
      },
      ...assetTypes.map((t) => ({
        key: t.slug,
        label: (
          <Space>
            <Text>{t.name}</Text>
          </Space>
        ),
      })),
      { type: "divider" },
      { key: "__blank", label: "Blank row" },
    ],
    onClick: ({ key }) => addRow(key === "__blank" ? null : key),
  };

  const hasLivestock = lineItems.some(
    (r) =>
      assetTypes.find((t) => t.slug === r.type_slug)?.is_livestock !== false,
  );
  const hasPhysical = lineItems.some(
    (r) =>
      assetTypes.find((t) => t.slug === r.type_slug)?.is_livestock === false,
  );
  const subtotal = lineItems.reduce((s, r) => {
    const cfg = assetTypes.find((t) => t.slug === r.type_slug) || {};
    return s + (cfg.is_batch ? r.quantity || 1 : 1) * (r.cost || 0);
  }, 0);

  return (
    <>
      <Card
        title={
          <Space>
            <FileTextOutlined />
            New Asset Purchase Invoice
          </Space>
        }
        extra={
          <Link to="/animal-assets">
            <Button onClick={onCancel} icon={<ArrowLeftOutlined />}>
              Back to Invoices
            </Button>
          </Link>
        }
        style={{ marginBottom: 20 }}
      >
        {/* Header Form */}
        <Card size="small" style={{ marginBottom: 12, background: "#fafafa" }}>
          <Form form={headerForm} layout="vertical">
            <Row gutter={12}>
              <Col xs={24} md={5}>
                <Form.Item
                  name="stakeholderId"
                  label="Supplier"
                  rules={[{ required: true }]}
                >
                  <Select
                    showSearch
                    placeholder="Select supplier"
                    optionFilterProp="children"
                    value={selectedStakeholder}
                    onChange={(value) => {
                      setSelectedStakeholder(value);
                      headerForm.setFieldsValue({ stakeholderId: value });
                    }}
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        <Divider style={{ margin: "4px 0" }} />
                        <div
                          style={{
                            padding: "4px 8px",
                            color: "#1677ff",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                          onClick={() => setStakeholderModalVisible(true)}
                        >
                          <PlusOutlined />
                          <span>Add supplier</span>
                        </div>
                      </>
                    )}
                  >
                    {suppliers.map((s) => (
                      <Option key={s.id} value={s.id}>
                        {s.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={12} md={3}>
                <Form.Item name="farm" label="Primary Farm">
                  <Select allowClear>
                    {farms.map((f) => (
                      <Option key={f.id} value={String(f.id)}>
                        {f.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={12} md={3}>
                <Form.Item
                  name="recorded"
                  label="Invoice Date"
                  rules={[{ required: true }]}
                  initialValue={dayjs()}
                >
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={12} md={3}>
                <Form.Item name="dueDate" label="Due Date">
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={12} md={3}>
                <Form.Item name="paymentMethod" label="Method">
                  <Select allowClear>
                    {["Cash", "Bank Transfer", "Cheque", "Mobile Money"].map(
                      (m) => (
                        <Option
                          key={m}
                          value={m.toLowerCase().replace(/ /g, "_")}
                        >
                          {m}
                        </Option>
                      ),
                    )}
                  </Select>
                </Form.Item>
              </Col>
              {/* <Col xs={12} md={12}>
                <Space
                  direction="horizontal"
                  style={{ width: "100%" }}
                  align="start"
                >
                  <Form.Item
                    name="deliveredMethod"
                    label="Delivery Method"
                  >
                    <Select onChange={(value) => setDeliveredMethod(value)}>
                      <Select.Option value="delivered">Delivered</Select.Option>
                    </Select>
                  </Form.Item>

                  {deliveredMethod === "delivered" && (
                    <Form.Item
                      name="deliveryPersonId"
                      label="Delivery Person"
                    >
                      <Select
                        placeholder="Select delivery person"
                        showSearch
                        optionFilterProp="children"
                      >
                        {users.map((e) => (
                          <Select.Option key={e.id} value={e.id}>
                            {e.firstname} {e.lastname}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                </Space>
              </Col> */}
              <Col xs={24}>
                <Form.Item name="remarks" label="Remarks">
                  <TextArea rows={3} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>

        {/* Line Items */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 8,
          }}
        >
          <Text strong>Line Items</Text>
          <Dropdown menu={addLineMenu} trigger={["click"]}>
            <Button type="dashed" icon={<PlusOutlined />} size="small">
              Add Item <DownOutlined />
            </Button>
          </Dropdown>
        </div>

        {lineItems.length === 0 ? (
          <div
            onClick={() => addRow(null)}
            style={{
              border: "2px dashed #d9d9d9",
              borderRadius: 8,
              padding: "28px 0",
              textAlign: "center",
              cursor: "pointer",
              color: "#aaa",
              marginBottom: 12,
            }}
          >
            <PlusOutlined
              style={{ fontSize: 20, marginBottom: 4, display: "block" }}
            />
            Click or use "Add Item ▾"
          </div>
        ) : (
          <div style={{ overflowX: "auto", marginBottom: 12 }}>
            <LineRowComponent
              lineItems={lineItems}
              updateRow={updateRow}
              removeRow={removeRow}
              farms={farms}
              silos={silos}
              assetTypes={assetTypes}
              subtypeMap={subtypeMap}
              previewTag={previewTag}
              hasLivestock={hasLivestock}
              hasPhysical={hasPhysical}
            />
          </div>
        )}

        {lineItems.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 24,
              padding: "8px 4px",
              borderTop: "1px solid #f0f0f0",
              marginBottom: 12,
            }}
          >
            <span>
              <Text type="secondary">Items: </Text>
              <Text strong>{lineItems.length}</Text>
            </span>
            <span>
              <Text type="secondary">Total: </Text>
              <Text strong style={{ fontSize: 16, color: "#1677ff" }}>
                {fmt(subtotal)}
              </Text>
            </span>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Popconfirm
            title="Save invoice?"
            description={`${lineItems.length} item(s) — Total: ${fmt(
              subtotal,
            )}`}
            onConfirm={saveInvoice}
          >
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              disabled={!lineItems.length}
            >
              Save Invoice
            </Button>
          </Popconfirm>
        </div>
      </Card>

      {/* Stakeholder Modal */}
      <StakeholderModal
        visible={stakeholderModalVisible}
        onCancel={() => setStakeholderModalVisible(false)}
        onSuccess={handleStakeholderAdded}
      />
    </>
  );
};

// LineRow Component
const LineRowComponent = ({
  lineItems,
  updateRow,
  removeRow,
  farms,
  silos,
  assetTypes,
  subtypeMap,
  previewTag,
  hasLivestock,
  hasPhysical,
}) => {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
      <thead>
        <tr
          style={{ background: "#fafafa", borderBottom: "1px solid #f0f0f0" }}
        >
          {[
            "Type",
            "Subtype",
            "Name",
            "Qty",
            "Unit Cost",
            "Total",
            ...(hasLivestock ? ["Birth", "Silo"] : []),
            ...(hasPhysical ? ["Asset Name", "Serial #", "Life (y)"] : []),
            "Farm",
            "",
          ].map((h) => (
            <th
              key={h}
              style={{
                padding: "6px 4px",
                textAlign: "left",
                fontWeight: 500,
                color: "#555",
                whiteSpace: "nowrap",
                fontSize: 11,
              }}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {lineItems.map((item, idx) => (
          <LineRow
            key={item._key}
            item={item}
            idx={idx}
            onChange={updateRow}
            onRemove={removeRow}
            farms={farms}
            silos={silos}
            assetTypes={assetTypes}
            subtypeMap={subtypeMap}
            onPreviewTag={previewTag}
          />
        ))}
      </tbody>
    </table>
  );
};

export default AssetPurchaseInvoice;
